import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';

let sentryModulePromise: Promise<typeof import('./lib/sentry')> | null = null;

function loadSentryModule() {
  if (sentryModulePromise === null) {
    sentryModulePromise = import('./lib/sentry');
  }

  return sentryModulePromise;
}

function reportClientError(error: Error, context: Record<string, unknown>) {
  void loadSentryModule()
    .then(({ captureException }) => {
      captureException(error, context);
    })
    .catch(() => {
      // Ignore reporting failures so error handling never cascades.
    });
}

function getRejectionDetails(reason: unknown): { name?: string; message?: string } {
  if (reason instanceof DOMException) {
    return { name: reason.name, message: reason.message };
  }

  if (reason instanceof Error) {
    return { name: reason.name, message: reason.message };
  }

  if (typeof reason === 'object' && reason !== null) {
    const maybeName = 'name' in reason && typeof reason.name === 'string' ? reason.name : undefined;
    const maybeMessage = 'message' in reason && typeof reason.message === 'string' ? reason.message : undefined;
    return { name: maybeName, message: maybeMessage };
  }

  return {};
}

function isSupabaseLockAbortError(reason: unknown): boolean {
  const { name, message } = getRejectionDetails(reason);
  return name === 'AbortError' && typeof message === 'string' && message.includes('signal is aborted without reason');
}

function scheduleBackgroundTask(task: () => void) {
  const runTask = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => task(), { timeout: 2_000 });
      return;
    }

    setTimeout(task, 1);
  };

  if (document.readyState === 'complete') {
    runTask();
    return;
  }

  window.addEventListener('load', runTask, { once: true });
}

// Capture uncaught browser/runtime issues and send to Sentry
window.addEventListener('error', (event) => {
  if (event.error instanceof Error) {
    reportClientError(event.error, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      userAgent: navigator.userAgent,
      href: window.location.href,
    });
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = (event as PromiseRejectionEvent).reason;
  if (isSupabaseLockAbortError(reason)) {
    event.preventDefault();
    return;
  }

  if (reason instanceof Error) {
    reportClientError(reason, {
      type: 'unhandledrejection',
      userAgent: navigator.userAgent,
      href: window.location.href,
    });
  } else {
    // For non-Error rejections, create an Error wrapper
    const error = new Error('Unhandled Promise Rejection');
    reportClientError(error, {
      reason: reason,
      userAgent: navigator.userAgent,
      href: window.location.href,
    });
  }
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

scheduleBackgroundTask(() => {
  const consentModule = import('./lib/cookieConsent');
  const sentryModule = loadSentryModule();
  const posthogModule = import('./lib/posthog');

  Promise.all([consentModule, sentryModule, posthogModule]).then(
    ([{ hasAnalyticsConsent, onConsentChange }, { initSentry, shutdownSentry }, { initPostHog, optOut }]) => {
      if (hasAnalyticsConsent()) {
        initSentry();
        initPostHog();
      }

      onConsentChange((categories) => {
        if (categories.includes('analytics')) {
          initSentry();
          initPostHog();
        } else {
          shutdownSentry();
          optOut();
        }
      });
    }
  ).catch(() => {
    // Ignore analytics initialization failures.
  });
});

scheduleBackgroundTask(() => {
  void Promise.allSettled([
    import('./lib/cpp/wasm-rsi').then(({ initWasmRSI }) => initWasmRSI()),
    import('./lib/cpp/wasm-kpi').then(({ initWasmKPI }) => initWasmKPI()),
    import('./lib/cpp/wasm-lorentzian').then(({ initWasmLorentzian }) => initWasmLorentzian()),
    import('./lib/cpp/wasm-backtest').then(({ initWasmBacktest }) => initWasmBacktest()),
    import('./lib/cpp/wasm-parameter-sweep').then(({ initWasmParameterSweep }) => initWasmParameterSweep()),
    import('./lib/cpp/wasm-monte-carlo').then(({ initWasmMonteCarlo }) => initWasmMonteCarlo()),
  ]);
});
